import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { locations } from '../mockData';
import { useT } from '../lib/i18n';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useT();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    location: 'Montolieu'
  });

  // Pre-fill subject when arriving from an "Inquire" button (e.g. /contact?subject=...)
  useEffect(() => {
    const preset = searchParams.get('subject');
    if (preset) {
      setFormData((prev) => ({ ...prev, subject: preset }));
      // Scroll the form into view on mobile so the user sees the pre-filled subject
      const el = document.querySelector('[data-testid="contact-form"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast({
        title: "Message Sent",
        description: "Thank you for contacting us. We'll respond within 24 hours.",
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        location: 'Montolieu'
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Please try again or email us directly at chimera_gallery@yahoo.ie';
      toast({
        title: "Could not send message",
        description: typeof detail === 'string' ? detail : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden bg-ivory">
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-[0.05] deco-pattern"></div>
        </div>
        
        <div className="relative z-10 text-center px-6">
          <div className="w-3 h-3 rotate-45 bg-gold mx-auto mb-6 border-2 border-dark-crimson"></div>
          <h1 className="text-7xl font-bold text-dark-green mb-6" style={{ fontFamily: 'Playfair Display, serif', letterSpacing: '0.1em' }}>
            {t('contact.title')}
          </h1>
          <div className="h-1 w-40 bg-dark-crimson mx-auto mt-6 mb-6"></div>
          <p className="text-dark-brown text-2xl max-w-2xl mx-auto" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {t('contact.subtitle')}
          </p>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="py-24 px-6 bg-cream deco-pattern">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <div className="mb-10">
                <div className="w-3 h-3 rotate-45 bg-gold mb-6 border-2 border-dark-crimson"></div>
                <h2 className="text-5xl font-bold text-dark-green mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Send us a Message
                </h2>
                <div className="h-1 w-32 bg-dark-crimson mb-8"></div>
                <p className="text-charcoal text-lg" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Fill out the form below and we'll get back to you shortly.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label className="block text-dark-crimson mb-3 font-bold tracking-wide" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.1rem' }}>{t('contact.name')} *</label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-ivory border-2 border-gold/40 text-dark-green py-6 rounded-none focus:border-dark-crimson transition-colors"
                    placeholder="Your full name"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  />
                </div>

                <div>
                  <label className="block text-dark-crimson mb-3 font-bold tracking-wide" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.1rem' }}>{t('contact.email')} *</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-ivory border-2 border-gold/40 text-dark-green py-6 rounded-none focus:border-dark-crimson transition-colors"
                    placeholder="your@email.com"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  />
                </div>

                <div>
                  <label className="block text-dark-crimson mb-3 font-bold tracking-wide" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.1rem' }}>{t('contact.phone')}</label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-ivory border-2 border-gold/40 text-dark-green py-6 rounded-none focus:border-dark-crimson transition-colors"
                    placeholder="+33 1 23 45 67 89"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  />
                </div>

                <div>
                  <label className="block text-dark-crimson mb-3 font-bold tracking-wide" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.1rem' }}>Preferred Location</label>
                  <select
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    className="w-full bg-ivory border-2 border-gold/40 text-dark-green py-4 px-4 rounded-none focus:border-dark-crimson transition-colors focus:outline-none"
                    style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1rem' }}
                  >
                    <option value="Montolieu">Montolieu, France</option>
                    <option value="Mullingar">Mullingar, Ireland</option>
                  </select>
                </div>

                <div>
                  <label className="block text-dark-crimson mb-3 font-bold tracking-wide" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.1rem' }}>{t('contact.subject')} *</label>
                  <Input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full bg-ivory border-2 border-gold/40 text-dark-green py-6 rounded-none focus:border-dark-crimson transition-colors"
                    placeholder="Inquiry about..."
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  />
                </div>

                <div>
                  <label className="block text-dark-crimson mb-3 font-bold tracking-wide" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.1rem' }}>{t('contact.message')} *</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full bg-ivory border-2 border-gold/40 text-dark-green rounded-none focus:border-dark-crimson transition-colors resize-none"
                    placeholder="Tell us about your inquiry..."
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="w-full bg-dark-crimson hover:bg-crimson text-cream py-6 rounded-none border-2 border-gold transition-all duration-300 hover:scale-105 text-lg font-bold tracking-wider disabled:opacity-60 disabled:hover:scale-100"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  {submitting ? t('contact.sending') : t('contact.submit')}
                </Button>
              </form>
            </div>

            {/* Location Info */}
            <div className="space-y-10">
              <div>
                <div className="w-3 h-3 rotate-45 bg-gold mb-6 border-2 border-dark-crimson"></div>
                <h2 className="text-5xl font-bold text-dark-green mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Our Locations
                </h2>
                <div className="h-1 w-32 bg-dark-crimson mb-8"></div>
              </div>

              {locations.map((location, index) => (
                <Card key={index} className="bg-ivory border-2 border-gold/50 p-10 hover:border-dark-crimson transition-all duration-300 hover:shadow-xl">
                  <h3 className="text-4xl font-bold text-dark-green mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {location.city}, {location.country}
                  </h3>
                  
                  <div className="space-y-5" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.05rem' }}>
                    <div className="flex items-start gap-4">
                      <MapPin className="text-dark-crimson mt-1 flex-shrink-0" size={22} />
                      <div>
                        <p className="text-charcoal/70 font-semibold mb-2">Address</p>
                        <p className="text-charcoal">{location.address}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <Phone className="text-dark-crimson mt-1 flex-shrink-0" size={22} />
                      <div>
                        <p className="text-charcoal/70 font-semibold mb-2">Phone</p>
                        <p className="text-gold font-bold">{location.phone}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <Mail className="text-dark-crimson mt-1 flex-shrink-0" size={22} />
                      <div>
                        <p className="text-charcoal/70 font-semibold mb-2">Email</p>
                        <p className="text-gold font-bold">chimera_gallery@yahoo.ie</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <Clock className="text-dark-crimson mt-1 flex-shrink-0" size={22} />
                      <div>
                        <p className="text-charcoal/70 font-semibold mb-2">Hours</p>
                        <p className="text-charcoal">{location.hours}</p>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}

              <Card className="bg-ivory border-2 border-gold/50 p-10">
                <h3 className="text-3xl font-bold text-dark-green mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Services
                </h3>
                <ul className="space-y-4 text-charcoal" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.05rem' }}>
                  <li className="flex items-center gap-4">
                    <div className="w-2 h-2 bg-gold rotate-45"></div>
                    Private viewings by appointment
                  </li>
                  <li className="flex items-center gap-4">
                    <div className="w-2 h-2 bg-gold rotate-45"></div>
                    Art acquisition consulting
                  </li>
                  <li className="flex items-center gap-4">
                    <div className="w-2 h-2 bg-gold rotate-45"></div>
                    Authentication & valuation
                  </li>
                  <li className="flex items-center gap-4">
                    <div className="w-2 h-2 bg-gold rotate-45"></div>
                    Restoration services
                  </li>
                  <li className="flex items-center gap-4">
                    <div className="w-2 h-2 bg-gold rotate-45"></div>
                    International shipping
                  </li>
                </ul>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
